import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faCircleXmark,
  faFilterCircleXmark,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { crmPostventaV2Api } from "../crmpostventa-v2/crmPostventaV2Api";
import EmbeddedProcessQueue from "./EmbeddedProcessQueue";
import "./crm-postventa.css";
import "../crmpostventa-v2/CRMPostventaV2Procesos.css";

const makeToday = () => new Date().toISOString().slice(0, 10);

const emptySummary = {
  onboardingPendientes: 0,
  zocoCheckActivos: 0,
  sinFacturacionActivos: 0,
  recuperoActivos: 0,
  extraviadasActivas: 0,
  proximosContactosVencidos: 0,
  alertasCriticas: 0,
  totalProcesosActivos: 0,
};

const queueSections = [
  {
    title: "Alertas de facturacion",
    processCode: "TERMINAL_SIN_FACTURACION_V2",
    processSlug: "terminal-sin-facturacion",
    viewAllPath: "/administracion/crm-postventa/seguimiento-2/sin-facturacion",
    emptyMessage: "No hay alertas de facturacion activas para mostrar.",
  },
  {
    title: "Onboarding",
    processCode: "ONBOARDING_V2",
    processSlug: "onboarding",
    viewAllPath: "/administracion/crm-postventa/seguimiento-2/onboarding",
  },
  {
    title: "Zoco Check",
    processCode: "ZOCO_CHECK_V2",
    processSlug: "zoco-check",
    viewAllPath: "/administracion/crm-postventa/seguimiento-2/zoco-check",
  },
  {
    title: "Sin facturacion",
    processCode: "TERMINAL_SIN_FACTURACION_V2",
    processSlug: "terminal-sin-facturacion",
    viewAllPath: "/administracion/crm-postventa/seguimiento-2/sin-facturacion",
  },
  {
    title: "Recupero",
    processCode: "RECUPERO_TERMINAL_V2",
    processSlug: "recupero-terminal",
    viewAllPath: "/administracion/crm-postventa/seguimiento-2/recupero",
  },
  {
    title: "Terminales extraviadas",
    processCode: "TERMINAL_EXTRAVIADA_V2",
    processSlug: "terminal-extraviada",
    viewAllPath: "/administracion/crm-postventa/seguimiento-2/extraviada",
  },
];

const priorityOptions = [
  { value: "", label: "Prioridad" },
  { value: "CRITICA", label: "Critica" },
  { value: "ALTA", label: "Alta" },
  { value: "MEDIA", label: "Media" },
  { value: "BAJA", label: "Baja" },
];

const stateOptions = [
  { value: "", label: "Estados" },
  { value: "PENDIENTE_CONTACTO_INICIAL", label: "Pendiente de contacto inicial" },
  { value: "PENDIENTE_PRIMER_CONTACTO", label: "Pendiente de contacto inicial" },
  { value: "PENDIENTE_PRIMER_WHATSAPP", label: "Primer WhatsApp" },
  { value: "ESPERANDO_SEGUNDO_WHATSAPP", label: "Segundo WhatsApp pendiente" },
  { value: "ESPERANDO_LLAMADA", label: "Llamada pendiente" },
  { value: "PENDIENTE_WHATSAPP_1", label: "Primer WhatsApp" },
  { value: "PENDIENTE_LLAMADA_1", label: "Primera llamada" },
  { value: "PENDIENTE_WHATSAPP_2", label: "Segundo WhatsApp" },
  { value: "PENDIENTE_LLAMADA_2", label: "Segunda llamada" },
  { value: "PENDIENTE_LLAMADA", label: "Llamada pendiente" },
  { value: "ESPERANDO_NUEVA_ALERTA", label: "Esperando nueva alerta" },
  { value: "REQUIERE_CORRECCION", label: "Requiere correccion" },
  { value: "CERRADO", label: "Cerrado" },
];

const normalizeSummaryError = (error) => {
  const normalized = String(error?.message || "").toLowerCase();
  if (normalized.includes("no tenes permisos")) {
    return "No tenes permisos para ver el resumen de alertas.";
  }

  return "No se pudo cargar el resumen superior.";
};

export default function CrmPostventaV2AlertsBoard({ darkMode = false }) {
  const [summary, setSummary] = useState(emptySummary);
  const [overview, setOverview] = useState({ total: 0 });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [draftFilters, setDraftFilters] = useState({
    fechaReferencia: makeToday(),
    prioridad: "",
    estado: "",
  });
  const [filters, setFilters] = useState({
    fechaReferencia: makeToday(),
    prioridad: "",
    estado: "",
    texto: "",
  });
  const [searchDraft, setSearchDraft] = useState("");
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const activeFilters = useMemo(
    () => Boolean(filters.prioridad || filters.estado || filters.texto),
    [filters.estado, filters.prioridad, filters.texto]
  );

  const displayDate = useMemo(() => {
    const raw = String(filters.fechaReferencia || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split("-");
      return `${day}/${month}/${year}`;
    }

    return raw || "-";
  }, [filters.fechaReferencia]);

  const lastUpdatedTime = useMemo(
    () => lastUpdated.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", hour12: false }),
    [lastUpdated]
  );

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setSummaryLoading(true);
      setSummaryError("");
      try {
        const [summaryResponse, overviewResponse] = await Promise.all([
          crmPostventaV2Api.obtenerResumenAlertas({ fechaReferencia: filters.fechaReferencia }),
          crmPostventaV2Api.obtenerAlertas({
            fechaReferencia: filters.fechaReferencia,
            prioridad: filters.prioridad || undefined,
            estado: filters.estado || undefined,
            texto: filters.texto || undefined,
            pagina: 1,
            tamanoPagina: 1,
          }),
        ]);
        if (cancelled) {
          return;
        }

        setSummary(summaryResponse || emptySummary);
        setOverview({ total: overviewResponse?.total || 0 });
        setLastUpdated(new Date());
      } catch (error) {
        console.error("Error cargando resumen de alertas V2", error);
        if (!cancelled) {
          setSummaryError(normalizeSummaryError(error));
          setOverview({ total: 0 });
        }
      } finally {
        if (!cancelled) {
          setSummaryLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [filters.estado, filters.fechaReferencia, filters.prioridad, filters.texto]);

  const handleApplyFilters = () => {
    setFilters({
      fechaReferencia: draftFilters.fechaReferencia,
      prioridad: draftFilters.prioridad,
      estado: draftFilters.estado,
      texto: String(searchDraft || "").trim(),
    });
  };

  const handleResetFilters = () => {
    setSearchDraft("");
    const reset = {
      fechaReferencia: makeToday(),
      prioridad: "",
      estado: "",
    };
    setDraftFilters(reset);
    setFilters({
      ...reset,
      texto: "",
    });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleApplyFilters();
    }
  };

  const primaryMetrics = [
    { label: "Total alertas criticas", value: summary.alertasCriticas || 0, className: "crm-alert-metric-card--critical-total" },
    { label: "Gestiones pendientes", value: overview.total || summary.totalProcesosActivos || 0, className: "crm-alert-metric-card--pending" },
    { label: "Proximos contactos vencidos", value: summary.proximosContactosVencidos || 0, className: "crm-alert-metric-card--overdue" },
    { label: "Comercios con falta de facturacion", value: summary.sinFacturacionActivos || 0, className: "crm-alert-metric-card--billing-gap" },
  ];

  const compactMetrics = [
    { label: "Seguimientos activos", value: summary.totalProcesosActivos || 0, tone: "default" },
    { label: "Bajo consumo sostenido", value: "Sin info", tone: "default" },
    { label: "Necesitan rollos", value: "Sin info", tone: "default" },
  ];

  return (
    <section className={`crm-postventa-page ${darkMode ? "is-dark" : ""}`} translate="no">
      <div className="container crm-postventa-shell py-4">
        <div className="crm-alert-center">
          <section className={`crm-alert-figma-board ${darkMode ? "is-dark" : ""}`}>
            <div className="crm-alert-header-row">
              <div className="crm-alert-title-block">
                <span className="crm-alert-kicker">CRM POSTVENTAS</span>
                <h2>Centro de alertas</h2>
              </div>
              <div className="crm-alert-pills-row">
                <span>Facturacion {summary.sinFacturacionActivos || 0}</span>
                <span>Criticas {summary.alertasCriticas || 0}</span>
                <span>Operativas manuales {overview.total || 0}</span>
                <span className="crm-alert-pills-strong">{displayDate} | {lastUpdatedTime}</span>
              </div>
            </div>

            <div className="crm-alert-toolbar-row">
              <Form.Control
                type="date"
                className="crm-postventa-filter-control crm-alert-toolbar-date"
                value={draftFilters.fechaReferencia}
                onChange={(event) => setDraftFilters((prev) => ({ ...prev, fechaReferencia: event.target.value }))}
                aria-label="Filtrar alertas por fecha"
              />
              <Form.Select
                className="crm-postventa-filter-control crm-alert-toolbar-select"
                value={draftFilters.prioridad}
                onChange={(event) => setDraftFilters((prev) => ({ ...prev, prioridad: event.target.value }))}
                aria-label="Filtrar alertas por prioridad"
              >
                {priorityOptions.map((item) => (
                  <option key={item.value || "all-priority"} value={item.value}>{item.label}</option>
                ))}
              </Form.Select>
              <Form.Select
                className="crm-postventa-filter-control crm-alert-toolbar-select"
                value={draftFilters.estado}
                onChange={(event) => setDraftFilters((prev) => ({ ...prev, estado: event.target.value }))}
                aria-label="Filtrar alertas por estado"
              >
                {stateOptions.map((item) => (
                  <option key={item.value || "all-state"} value={item.value}>{item.label}</option>
                ))}
              </Form.Select>
              <div className="crm-alert-search-wrap crm-alert-toolbar-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                <Form.Control
                  type="search"
                  className="crm-postventa-filter-control"
                  placeholder="Buscar por comercio, CUIT o estado..."
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  aria-label="Buscar alertas"
                />
                {String(searchDraft || "").trim() ? (
                  <button type="button" className="crm-alert-search-clear" onClick={() => setSearchDraft("")} aria-label="Limpiar busqueda">
                    <FontAwesomeIcon icon={faCircleXmark} />
                  </button>
                ) : null}
              </div>
              <button type="button" className="crm-alert-refresh-icon" onClick={handleApplyFilters} aria-label="Actualizar alertas">
                <FontAwesomeIcon icon={faArrowsRotate} />
              </button>
            </div>

            <div className="crm-alert-metrics-shell">
              <div className="crm-alert-metrics-grid">
                {primaryMetrics.map((item) => (
                  <article className={`crm-alert-metric-card ${item.className} ${darkMode ? "is-dark" : ""}`} key={item.label}>
                    <button type="button" className="crm-alert-help-dot" aria-label={`Informacion de ${item.label}`}>?</button>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </article>
                ))}
              </div>
              <div className="crm-alert-compact-metrics">
                {compactMetrics.map((item) => (
                  <article className={`crm-alert-compact-card tone-${item.tone || "default"} ${darkMode ? "is-dark" : ""}`} key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </article>
                ))}
              </div>
            </div>

            {summaryLoading ? (
              <section className={`crm-postventa-panel crm-alert-surface ${darkMode ? "is-dark" : ""}`}>
                <Spinner animation="border" size="sm" className="me-2" />
                Cargando resumen...
              </section>
            ) : summaryError ? (
              <section className={`crm-postventa-panel crm-alert-surface ${darkMode ? "is-dark" : ""}`}>
                <Alert variant="secondary" className="mb-0">
                  {summaryError}
                </Alert>
              </section>
            ) : null}
          </section>

          <div className="crm-postventa-alert-blocks">
            {queueSections.map((item) => (
              <section
                key={item.title}
                className={`crm-postventa-panel crm-alert-surface ${
                  item.processCode === "TERMINAL_SIN_FACTURACION_V2" && item.title === "Alertas de facturacion"
                    ? "crm-alert-surface--billing-flat"
                    : ""
                } ${
                  item.processCode === "TERMINAL_EXTRAVIADA_V2" && item.title === "Terminales extraviadas"
                    ? "crm-alert-surface--lost-terminal-flat"
                    : ""
                } ${
                  item.processCode === "ONBOARDING_V2" && item.title === "Onboarding"
                    ? "crm-alert-surface--onboarding-flat"
                    : ""
                } ${
                  item.processCode === "ZOCO_CHECK_V2" && item.title === "Zoco Check"
                    ? "crm-alert-surface--zoco-check-flat"
                    : ""
                } ${darkMode ? "is-dark" : ""}`.trim()}
              >
                <EmbeddedProcessQueue
                  title={item.title}
                  processCode={item.processCode}
                  processSlug={item.processSlug}
                  viewAllPath={item.viewAllPath}
                  maxRows={5}
                  filters={filters}
                  emptyMessage={item.emptyMessage}
                  hideContainerCard
                  className="crm-postventa-alert-queue"
                />
              </section>
            ))}
          </div>

          {activeFilters ? (
            <div className="crm-alert-floating-actions">
              <Button variant="outline-secondary" size="sm" onClick={handleResetFilters}>
                <FontAwesomeIcon className="me-2" icon={faFilterCircleXmark} />
                Limpiar filtros
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
